import moment from "moment";
import React, { useEffect, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  IconButton,
  Grid,
  Button,
} from "@mui/material";
import { toast } from "react-toastify";

import BlockIcon from "@mui/icons-material/Block";
import { useAppDispatch } from "Redux/hooks";
import { updateProfileAsync } from "Redux/Reducers/sellerProfile";
import { useSession } from "Context/userAuthContext";
import _ from "lodash";

type CalCellProps = {
  dateFullString: string;
  date: number;
  highlighted?: boolean;
  isFirstDay?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
};

export const CalCell = ({
  dateFullString,
  date,
  highlighted,
  isFirstDay,
  disabled,
  children,
}: CalCellProps) => {
  const { user, sellerProfile } = useSession();
  const dispatch = useAppDispatch();
  const [blocked, setBlocked] = useState(
    disabled ||
      sellerProfile.availability.dateBlocked.findIndex(
        (d) => d === dateFullString
      ) !== -1
  );

  useEffect(() => {
    setBlocked(
      disabled ||
        sellerProfile.availability.dateBlocked.findIndex(
          (d) => d === dateFullString
        ) !== -1
    );
  }, [sellerProfile.availability.dateBlocked]);

  const toggleBlocked = () => {
    setBlocked(!blocked);
    let dateBlockedCopy = _.clone(sellerProfile.availability.dateBlocked);
    const currentDate = new Date();
    const twoDaysAgo = new Date();
    twoDaysAgo.setDate(currentDate.getDate() - 2);

    // only keep last 2 days
    dateBlockedCopy = dateBlockedCopy.filter((d) => {
      const blockedDate = new Date(d);
      return blockedDate >= twoDaysAgo;
    });

    if (blocked) {
      toast(
        `${moment(dateFullString).format("MMM DD, YYYY")} was unblocked from your availability.`,
        {
          position: "top-center",
          autoClose: 1600,
        }
      );
      dateBlockedCopy = dateBlockedCopy.filter((d) => d !== dateFullString);
    } else {
      toast(
        `${moment(dateFullString).format("MMM DD, YYYY")} was blocked from your availability.`,
        {
          position: "top-center",
          autoClose: 1600,
        }
      );
      dateBlockedCopy.push(dateFullString);
    }
    if (user) {
      dispatch(
        updateProfileAsync({
          uid: user?.uid,
          sellerProfile: {
            ...sellerProfile,
            availability: {
              ...sellerProfile.availability,
              dateBlocked: dateBlockedCopy,
            },
          },
        })
      );
    }
  };

  return (
    <Grid
      item
      xs={12 / 7}
      sx={{
        "&:hover .blockedIndicator": {
          transition: "opacity .5s ease",
          opacity: 1,
        },
        "&:hover": {
          transition: "opacity .5s ease",
          boxShadow: disabled ? "none" : "rgba(0, 0, 0, 0.08) 0px 2px 10px 0px",
          border: disabled
            ? ".5px solid rgba(0, 0, 0, 0.12)"
            : ".5px solid rgba(0, 0, 0, 0)",
          borderLeft:
            isFirstDay && !disabled
              ? "1.5px solid rgba(0, 0, 0, 0.6)"
              : ".5px solid rgba(0, 0, 0, 0)",
          zIndex: 10,
        },
        border: ".5px solid rgba(0, 0, 0, 0.12)",
        borderLeft:
          isFirstDay && !disabled
            ? "1.5px solid rgba(0, 0, 0, 0.6)"
            : ".5px solid rgba(0, 0, 0, 0.12)",
        minHeight: "100px",
        p: 1,
        backgroundColor: disabled || blocked ? "background.default" : "white",
      }}
    >
      <Stack direction="row" alignItems="center" sx={{ mb: 0.5 }}>
        <Stack
          flex={1}
          flexDirection="row"
          alignItems="center"
          columnGap={highlighted ? 1 : 0}
        >
          {isFirstDay ? (
            <Typography
              variant="body1"
              sx={{
                color: "text.primary",
                opacity: disabled ? 0.5 : 1,
              }}
            >
              {moment(dateFullString).format("MMM")}
            </Typography>
          ) : null}
          <Stack
            sx={{
              backgroundColor: highlighted ? "primary.main" : "transparent",
              width: "30px",
              height: "30px",
              textAlign: "center",
              justifyContent: "center",
              borderRadius: 1,
            }}
          >
            <Typography
              variant="body1"
              sx={{
                color: highlighted ? "white" : "text.primary",
                fontWeight: highlighted ? 500 : 400,
                opacity: disabled && !highlighted ? 0.5 : 1,
              }}
            >
              {date}
            </Typography>
          </Stack>
        </Stack>
        <Stack alignItems="center" flexDirection="row" columnGap={0.5}>
          <Box className="blockedIndicator" sx={{ opacity: 0 }}>
            {blocked ? null : (
              <Button
                size="small"
                color="secondary"
                onClick={toggleBlocked}
                disabled={disabled}
                sx={{
                  height: "25.75px",
                  transition: "opacity .5s ease",
                  opacity: disabled ? 0 : 0.3,
                  "&:hover": {
                    opacity: disabled ? 0 : 1,
                  },
                }}
              >
                {blocked ? "Unblock" : "Block"}
              </Button>
            )}
          </Box>
          <IconButton
            size="small"
            disabled={disabled}
            onClick={toggleBlocked}
            sx={{
              height: "25.75px",
              transition: "opacity .5s ease",
              opacity: disabled && !highlighted ? 0 : 0.8,
              display: blocked && !highlighted ? "block" : "none",
              "&:hover .showLabel": { opacity: 1 },
            }}
          >
            <BlockIcon fontSize="inherit" color="secondary" />
          </IconButton>
        </Stack>
      </Stack>
      <Stack spacing={1}>{children}</Stack>
    </Grid>
  );
};
